// Date-only helpers. JavaScript's built-in Date is a datetime with an
// implicit timezone. A "date-only" value (assessment date, due date,
// date of birth) must never pass through UTC conversion — otherwise a
// user east of UTC will see the day before the one they picked.
//
// Two footguns this file fixes:
//   1) `new Date().toISOString().split("T")[0]` — reads local wall-clock,
//      converts to UTC, then slices. In the evening east of UTC this
//      returns tomorrow; west of UTC it returns yesterday.
//   2) `new Date("2026-07-28").toLocaleDateString()` — JS parses a bare
//      ISO date string as UTC midnight, then renders it in local time,
//      shifting west-of-UTC users back by one day.
//
// The rule: date-only values are `YYYY-MM-DD` strings on the wire and
// only ever become a JS Date via `parseDateOnly` (which anchors to local
// midnight). They only ever leave a Date via `formatDateOnly` (which
// reads the local Y/M/D components, not the UTC ones).

/**
 * Today's calendar date in the user's local timezone as `YYYY-MM-DD`.
 * Never round-trips through UTC.
 */
export const todayLocal = (): string => formatDateOnly(new Date());

/**
 * Format any Date | Dayjs | string as a `YYYY-MM-DD` local date string.
 * When passed a Date, reads local components (not UTC). When passed a
 * `YYYY-MM-DD` string, returns it as-is.
 */
export const formatDateOnly = (v: unknown): string => {
  if (v == null || v === "") return "";
  if (typeof v === "string") {
    // Already a bare YYYY-MM-DD — pass through unchanged.
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
    // ISO datetime — take the date portion in the LOCAL tz to avoid the
    // "sent at 8 PM in EDT stored as tomorrow" pitfall.
    const d = new Date(v);
    if (Number.isFinite(d.getTime())) return formatDateOnly(d);
    return "";
  }
  // Dayjs-like (has format())
  if (v && typeof (v as any).format === "function") {
    try { return (v as any).format("YYYY-MM-DD"); } catch { /* fall through */ }
  }
  if (v instanceof Date) {
    const y = v.getFullYear();
    const m = String(v.getMonth() + 1).padStart(2, "0");
    const d = String(v.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  return "";
};

/**
 * Parse a `YYYY-MM-DD` string into a Date anchored at local midnight.
 * NEVER use `new Date("YYYY-MM-DD")` — that anchors to UTC midnight
 * which becomes the previous day west of UTC when rendered locally.
 */
export const parseDateOnly = (s: string | null | undefined): Date | null => {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) {
    const d = new Date(s);
    return Number.isFinite(d.getTime()) ? d : null;
  }
  const [, y, mo, dd] = m;
  return new Date(Number(y), Number(mo) - 1, Number(dd));
};

/**
 * Present a `YYYY-MM-DD` string with an Intl locale format ("Jul 28, 2026")
 * WITHOUT shifting by the local timezone offset. Safe for any timezone.
 */
export const presentDateOnly = (
  s: string | null | undefined,
  opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" },
  locale: string = "en-US"
): string => {
  const d = parseDateOnly(s ?? undefined);
  return d ? d.toLocaleDateString(locale, opts) : "";
};
